import type { Agent } from '@deskworks/shared-types';
import { useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { api, errorMessage } from '@/lib/api';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { useToast } from '@/lib/toast';

/**
 * Delete an agent; the backend answers 409 (lead of a desk, open work items,
 * running worker) — shown inside the dialog so the user knows what to fix.
 */
export function DeleteAgentButton({
  agent,
  workspaceId,
  onDeleted,
  children,
}: {
  agent: Pick<Agent, 'id' | 'name'>;
  workspaceId: string;
  onDeleted?: () => void;
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onDelete = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.agents.delete(agent.id);
      queryClient.removeQueries({ queryKey: qk.agent(agent.id) });
      void queryClient.invalidateQueries({ queryKey: qk.agents(workspaceId) });
      void queryClient.invalidateQueries({ queryKey: qk.desks(workspaceId) });
      toast.success(t('Agent deleted'));
      setOpen(false);
      onDeleted?.();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) setError(null);
      }}
    >
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('Delete agent “{name}”?', { name: agent.name })}</AlertDialogTitle>
          <AlertDialogDescription>
            {t(
              'The agent is removed from all desks, together with its conversations and run history. Past work items stay, without an assigned agent. This cannot be undone.',
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error && (
          <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm break-words text-destructive">
            {error}
          </p>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel>{t('Cancel')}</AlertDialogCancel>
          <Button variant="destructive" loading={busy} onClick={() => void onDelete()}>
            {t('Delete agent')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function DeleteAgentIcon(props: Omit<Parameters<typeof DeleteAgentButton>[0], 'children'>) {
  return (
    <DeleteAgentButton {...props}>
      <Button
        variant="ghost"
        size="icon-sm"
        className="text-muted-foreground hover:text-destructive"
        aria-label={t('Delete {name}', { name: props.agent.name })}
      >
        <Trash2 />
      </Button>
    </DeleteAgentButton>
  );
}
